/**
 * Utility functions for user search functionality
 */

/**
 * Normalize API search results to ensure user_id exists
 * @param {Array} results - Raw API results
 * @returns {Array} - Normalized results with user_id
 */
export const normalizeSearchResults = (results) => {
  if (!results || !Array.isArray(results)) {
    return [];
  }

  return results.map((user) => ({
    ...user,
    // Use user_id if it exists (including 0), otherwise fall back to id.
    // This is necessary because following/followers returns user.user_id
    // but search returns user.id so it needs to be normalized
    user_id: user.user_id !== undefined ? user.user_id : user.id,
  }));
};

/**
 * Filter out invalid users (e.g., user_id of '0' represents "Deleted User")
 * Also filters out users with null, undefined, or empty user_id
 * @param {Array} users - Array of user objects
 * @returns {Array} - Filtered array without invalid users
 */
export const sanitizeUsers = (users) => {
  if (!users || !Array.isArray(users)) {
    return [];
  }

  return users.filter((user) => {
    const userId = user?.user_id;
    // Filter out null, undefined, empty string, and '0' (deleted user)
    if (userId === null || userId === undefined || userId === '') {
      return false;
    }
    // Convert to string and check for '0'
    return String(userId) !== '0';
  });
};

/**
 * Filter users by search query (matches display_name or username)
 * @param {Array} users - Array of user objects
 * @param {string} query - Search query
 * @returns {Array} - Filtered array of users matching the query
 */
export const filterUsersByQuery = (users, query) => {
  if (!users || !Array.isArray(users)) {
    return [];
  }

  const normalizedQuery = query?.trim().toLowerCase() || '';
  if (!normalizedQuery) {
    return users;
  }

  return users.filter((user) => {
    const displayName = user.display_name?.toLowerCase() || '';
    const username = user.username?.toLowerCase() || '';
    return displayName.includes(normalizedQuery) || username.includes(normalizedQuery);
  });
};

/**
 * Build a lookup object from users array, keyed by user_id
 * @param {Array} usersArrays - One or more arrays of user objects to merge into lookup
 * @returns {Object} - Lookup object { [user_id]: user }
 */
export const buildUserLookup = (...usersArrays) => {
  const lookup = {};

  usersArrays.forEach((users) => {
    if (!users || !Array.isArray(users)) {
      return;
    }

    users.forEach((user) => {
      if (user?.user_id && !lookup[user.user_id]) {
        lookup[user.user_id] = user;
      }
    });
  });

  return lookup;
};

/**
 * Process API search results: normalize and sanitize
 * @param {Object} results - API response with items array
 * @returns {Array} - Processed user array
 */
export const processSearchResults = (results) => {
  const normalized = normalizeSearchResults(results?.items || []);
  return sanitizeUsers(normalized);
};
